import React, { useCallback, useEffect, useState } from "react";
import { api, ApiError, staleCopy } from "../api";
import type { McpAccount, McpCatalogEntry, McpConnection, McpList, ModelSpec, ProfileModelState, ProfileSummary, ProviderModels, ResourceSummary } from "../types";
import { StaleNotice } from "../components/StaleNotice";
import { ACCOUNT_LABELS } from "../../../shared/wire/mcp.ts";
import { providerChoice, THINKING_LEVELS, thinkingLevelOf } from "./modelChoice";

/**
 * What a profile is configured to be, and the two parts of it you can change from here: its
 * default model and which MCP servers it gets.
 *
 * Everything shown comes from the profile itself, never from a running session, so this opens for
 * a profile you have never talked to. The session-dependent half — whether a server actually
 * connected this time — is deliberately elsewhere: a server that fails to start already raises a
 * diagnostic (`src/runtime/mcp.ts`) that the chat shows as an error banner, which is where a
 * failure that just happened belongs.
 */

/** A titled block with a count and, sometimes, the one action that belongs to it. */
function Section(props: { title: string; count?: number; action?: React.ReactNode; children: React.ReactNode }) {
	return (
		<section className="cfg-section">
			<header>
				<h3>
					{props.title}
					{props.count !== undefined && <span className="cfg-count">{props.count}</span>}
				</h3>
				{props.action}
			</header>
			{props.children}
		</section>
	);
}

/** Key and value on one line. Used only for the short facts, where a bounded row would be noise. */
function Fact(props: { label: string; children: React.ReactNode }) {
	return (
		<div className="cfg-fact">
			<span className="k">{props.label}</span>
			<span className="v">{props.children}</span>
		</div>
	);
}

/**
 * One item in a list, with its name on its own line above its description.
 *
 * This is the whole fix for the panel being hard to read. Before, a name chip and a three-line
 * description sat in one column with a 5px gap, so a list of three skills was one wall of text
 * with no way to find where an entry started.
 */
function Row(props: {
	name: React.ReactNode;
	tag?: string;
	note?: string;
	action?: React.ReactNode;
	/** A line below the description — used for a server's sign-in state. */
	extra?: React.ReactNode;
}) {
	return (
		<li className="cfg-row">
			<div className="cfg-row-head">
				<span className="cfg-row-name">{props.name}</span>
				{props.tag && <span className="cfg-row-tag">{props.tag}</span>}
				{props.action}
			</div>
			{props.note && <p className="cfg-row-note">{props.note}</p>}
			{props.extra}
		</li>
	);
}

function ResourceRows({ items, prefix = "" }: { items: ResourceSummary[]; prefix?: string }) {
	return (
		<ul className="cfg-rows">
			{items.map((r) => (
				<Row key={`${r.source ?? ""}${r.name}`} name={<code>{`${prefix}${r.name}`}</code>} tag={r.source} note={r.description} />
			))}
		</ul>
	);
}

/** Both null, or the same provider, model and thinking level. */
function sameModel(a: ModelSpec | null, b: ModelSpec | null): boolean {
	if (!a || !b) return a === b;
	return a.provider === b.provider && a.name === b.name && a.thinking === b.thinking;
}

/**
 * The profile's shared default model: what its sessions run on unless a person picks another in the
 * chat. A working copy like the MCP list: the selects change it, 保存 commits it, and 恢复平台默认
 * saves no model at all, so config/app.yaml's default applies.
 */
function ModelSection({ profile, onSaved }: { profile: ProfileSummary; onSaved: () => void }) {
	const [providers, setProviders] = useState<ProviderModels[]>([]);
	/** The saved default the working copy started from, and its version. */
	const [base, setBase] = useState<ProfileModelState>({ model: profile.model, version: profile.modelVersion, effectiveModel: profile.effectiveModel });
	/** The working copy; null is the platform default. */
	const [choice, setChoice] = useState<ModelSpec | null>(profile.model);
	/** A save refused because the default changed after it was loaded: what it holds now, and what was being saved. */
	const [stale, setStale] = useState<{ current: ProfileModelState | null; model: ModelSpec | null } | null>(null);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		api
			.models()
			.then((r) => setProviders(r.providers))
			.catch(() => setProviders([]));
	}, []);

	const take = useCallback((state: ProfileModelState) => {
		setBase(state);
		setChoice(state.model);
		setStale(null);
	}, []);

	// A save elsewhere, or switching profiles, makes the working copy stale; a reload that leaves the version alone does not.
	const { model, modelVersion, effectiveModel } = profile;
	useEffect(() => {
		take({ model, version: modelVersion, effectiveModel });
	}, [profile.name, modelVersion, take]);

	/** Save `model` over the default the working copy started from, or over `ifVersion` once the person chose to overwrite. */
	async function save(model: ModelSpec | null, ifVersion = base.version) {
		setSaving(true);
		setError(null);
		setStale(null);
		try {
			take(await api.setProfileModel(profile.name, model, ifVersion));
			onSaved();
		} catch (err) {
			const copy = staleCopy<ProfileModelState>(err, 409);
			if (copy) setStale({ ...copy, model });
			else {
				// A refused choice never took effect, so the selects go back to what is saved.
				setChoice(base.model);
				setError(err instanceof ApiError ? err.message : "保存失败");
			}
		} finally {
			setSaving(false);
		}
	}

	const entry = choice ? providers.find((p) => p.provider === choice.provider) : undefined;
	// Unknown until the catalog arrives, and then only hidden when the model is known not to think.
	const thinks = entry?.models.find((m) => m.id === choice?.name)?.reasoning ?? true;
	const saved = base.effectiveModel;

	return (
		<Section title="默认模型">
			{error && <p className="banner error">{error}</p>}
			{stale && (
				<StaleNotice
					onReload={() => (stale.current ? take(stale.current) : onSaved())}
					onOverwrite={() => void save(stale.model, stale.current?.version)}
				/>
			)}

			<div className="cfg-facts">
				<Fact label="当前">
					{`${saved.name} · ${saved.thinking}`}
					<span className="cfg-note">
						{base.model ? saved.provider : "平台默认"}
					</span>
				</Fact>
			</div>

			<div className="cfg-model">
				<label>
					<span>provider</span>
					<select value={choice?.provider ?? ""} onChange={(e) => setChoice(providerChoice(e.target.value, providers, choice ?? saved, choice?.thinking ?? saved.thinking))}>
						<option value="">平台默认</option>
						{providers.map((p) => (
							<option key={p.provider} value={p.provider}>
								{p.name}
								{p.subscription && "（订阅）"}
							</option>
						))}
						{choice && !providers.some((p) => p.provider === choice.provider) && <option value={choice.provider}>{choice.provider}</option>}
					</select>
				</label>
				<label>
					<span>模型</span>
					<select value={choice?.name ?? ""} disabled={!choice} onChange={(e) => choice && setChoice({ ...choice, name: e.target.value })}>
						{!choice && <option value="">平台默认</option>}
						{entry?.models.map((m) => (
							<option key={m.id} value={m.id}>
								{m.id}
							</option>
						))}
						{choice && !entry?.models.some((m) => m.id === choice.name) && <option value={choice.name}>{choice.name}</option>}
					</select>
				</label>
				{thinks && (
					<label>
						<span>思考</span>
						<select
							value={choice?.thinking ?? ""}
							disabled={!choice}
							onChange={(e) => choice && setChoice({ ...choice, thinking: thinkingLevelOf(e.target.value) })}
						>
							{!choice && <option value="">平台默认</option>}
							{choice &&
								THINKING_LEVELS.map((level) => (
									<option key={level} value={level}>
										{level}
									</option>
								))}
						</select>
					</label>
				)}
			</div>

			<div className="cfg-save">
				<button onClick={() => void save(choice)} disabled={saving || sameModel(choice, base.model)}>
					{saving ? "保存中…" : "保存"}
				</button>
				<button className="ghost" onClick={() => void save(null)} disabled={saving || base.model === null}>
					恢复平台默认
				</button>
			</div>
		</Section>
	);
}

/**
 * The MCP section owns its own state, as the model section does.
 *
 * Selection is a working copy: 添加 and × change it, 保存 commits it. One write and one registry
 * reload per intent, and a half-made choice is never briefly the profile's real configuration.
 */
function McpSection({ profile, onSaved }: { profile: ProfileSummary; onSaved: () => void }) {
	const [catalog, setCatalog] = useState<McpCatalogEntry[] | null>(null);
	const [connections, setConnections] = useState<McpConnection[]>([]);
	const [connecting, setConnecting] = useState<{ server: string; account: McpAccount } | null>(null);
	const [selected, setSelected] = useState<string[]>(profile.mcpCatalogIds);
	/** The list the working copy started from, and its version. */
	const [base, setBase] = useState<McpList>({ servers: profile.mcpCatalogIds, version: profile.mcpVersion });
	/** A save refused because the list changed after it was loaded, and what it holds now. */
	const [stale, setStale] = useState<{ current: McpList | null } | null>(null);
	const [picking, setPicking] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const refreshConnections = useCallback(
		() =>
			api
				.mcpConnections()
				.then((r) => setConnections(r.connections))
				.catch(() => {}),
		[],
	);

	useEffect(() => {
		api
			.mcpCatalog()
			.then((r) => setCatalog(r.servers))
			.catch(() => setCatalog([]));
		void refreshConnections();
	}, [refreshConnections]);

	/**
	 * While a consent tab is open there is nothing to listen to — the callback lands on the server,
	 * in a different tab, and this window is never told. Polling is the honest mechanism; it stops
	 * the moment the row turns connected, and after two minutes regardless.
	 */
	useEffect(() => {
		if (!connecting) return;
		const started = Date.now();
		const timer = setInterval(() => {
			if (Date.now() - started > 120_000) {
				setConnecting(null);
				return;
			}
			void api.mcpConnections().then((r) => {
				setConnections(r.connections);
				const conn = r.connections.find((x) => x.server === connecting.server);
				if (conn?.accounts.find((a) => a.account === connecting.account)?.connected) setConnecting(null);
			});
		}, 2000);
		return () => clearInterval(timer);
	}, [connecting]);

	async function connect(server: string, account: McpAccount) {
		setError(null);
		try {
			const { authorizationUrl } = await api.startMcpAuth(server, account);
			if (!authorizationUrl) {
				await refreshConnections();
				return;
			}
			// A new tab, not this one: leaving the panel would lose the unsaved selection beside it.
			window.open(authorizationUrl, "_blank", "noopener");
			setConnecting({ server, account });
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "连接失败");
		}
	}

	async function disconnect(server: string, account: McpAccount) {
		setError(null);
		try {
			await api.disconnectMcp(server, account);
			await refreshConnections();
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "断开失败");
		}
	}

	/** The sign-in line under a server that has one; servers without OAuth render nothing here. */
	function authLine(id: string) {
		const conn = connections.find((x) => x.server === id);
		if (!conn) return null;
		if (conn.missing.length > 0) return <span className="cfg-auth missing">需要 {conn.missing.join(", ")}</span>;
		// Name the account only when there is more than one to tell apart.
		const named = conn.accounts.length > 1;
		return conn.accounts.map(({ account, connected }) => {
			const waiting = connecting?.server === id && connecting.account === account;
			return (
				<span key={account} className="cfg-auth">
					{named && `${ACCOUNT_LABELS[account]}：`}
					{connected ? "已连接" : waiting ? "等待授权…" : "未连接"}
					{connected ? (
						<button className="ghost cfg-action" onClick={() => void disconnect(id, account)}>
							断开
						</button>
					) : (
						<button className="ghost cfg-action" onClick={() => void connect(id, account)} disabled={waiting}>
							连接
						</button>
					)}
				</span>
			);
		});
	}

	const take = useCallback((list: McpList) => {
		setSelected(list.servers);
		setBase(list);
		setStale(null);
	}, []);

	// A save elsewhere, or switching profiles, makes the working copy stale.
	useEffect(() => {
		take({ servers: profile.mcpCatalogIds, version: profile.mcpVersion });
	}, [profile.name, profile.mcpCatalogIds, profile.mcpVersion, take]);

	// Servers the profile's own mcp.json declares. They are real and the agent gets them, but they
	// are not the browser's to remove — nothing here can edit that file.
	const local = profile.mcpServers.filter((s) => !profile.mcpCatalogIds.includes(s));
	const byId = new Map((catalog ?? []).map((e) => [e.id, e]));
	const unpicked = (catalog ?? []).filter((e) => !selected.includes(e.id));
	const dirty = JSON.stringify(selected) !== JSON.stringify(base.servers);

	/** Save over the list the working copy started from, or over `ifVersion` once the person chose to overwrite. */
	async function save(ifVersion = base.version) {
		setSaving(true);
		setError(null);
		setStale(null);
		try {
			take(await api.setProfileMcp(profile.name, selected, ifVersion));
			onSaved();
		} catch (err) {
			const copy = staleCopy<McpList>(err);
			if (copy) setStale(copy);
			else setError(err instanceof ApiError ? err.message : "保存失败");
		} finally {
			setSaving(false);
		}
	}

	return (
		<Section
			title="MCP"
			count={selected.length + local.length}
			action={
				<button className="ghost cfg-action" onClick={() => setPicking((v) => !v)} disabled={catalog === null}>
					{picking ? "取消" : "添加"}
				</button>
			}
		>
			{error && <p className="banner error">{error}</p>}
			{stale && (
				<StaleNotice
					onReload={() => (stale.current ? take(stale.current) : onSaved())}
					onOverwrite={() => void save(stale.current?.version)}
				/>
			)}

			{picking && (
				<ul className="cfg-rows cfg-picker">
					{catalog !== null && unpicked.length === 0 && <p className="hint">暂无可添加项</p>}
					{unpicked.map((entry) => (
						<Row
							key={entry.id}
							name={entry.label}
							note={entry.description}
							tag={entry.available ? undefined : `需要 ${entry.requires.join(", ")}`}
							action={
								<button
									className="ghost cfg-action"
									// Unavailable is advice, not a gate: the server still starts and still
									// fails loudly, and blocking the click would leave no way to set it up
									// first and enable it after.
									title={entry.available ? undefined : `缺少 ${entry.requires.join(", ")}`}
									onClick={() => {
										setSelected((v) => [...v, entry.id]);
										setPicking(false);
									}}
								>
									添加
								</button>
							}
						/>
					))}
				</ul>
			)}

			{selected.length === 0 && local.length === 0 && !picking && <p className="hint">暂无 MCP 服务</p>}

			<ul className="cfg-rows">
				{selected.map((id) => (
					<Row
						key={id}
						name={byId.get(id)?.label ?? id}
						note={byId.get(id)?.description}
						tag={byId.has(id) ? undefined : "不在 catalog 中"}
						extra={authLine(id)}
						action={
							<button className="ghost cfg-action" title="移除" onClick={() => setSelected((v) => v.filter((x) => x !== id))}>
								移除
							</button>
						}
					/>
				))}
				{local.map((name) => (
					<Row key={name} name={<code>{name}</code>} tag="来自 mcp.json" />
				))}
			</ul>

			{dirty && (
				<div className="cfg-save">
					<button onClick={() => void save()} disabled={saving}>
						{saving ? "保存中…" : "保存"}
					</button>
				</div>
			)}
		</Section>
	);
}

export function ProfileConfig({
	profile,
	onClose,
	onManageSkills,
	onChanged,
}: {
	profile: ProfileSummary;
	onClose: () => void;
	onManageSkills: () => void;
	onChanged: () => void;
}) {
	return (
		<div className="modal" role="dialog" aria-label={`${profile.name} 的配置`}>
			<div className="modal-card config-card">
				<header>
					<span>
						<b>{profile.name}</b> 的配置
					</span>
					<button className="ghost" onClick={onClose}>
						关闭
					</button>
				</header>

				<div className="config-body">
					<ModelSection profile={profile} onSaved={onChanged} />

					<Section title="运行">
						<div className="cfg-facts">
							{profile.origin === "builder" && (
								<Fact label="来源">
									builder 草稿
								</Fact>
							)}
							<Fact label="工作区">
								{profile.workspace.mode === "persistent" ? "持久" : "每会话"}
							</Fact>
						</div>
					</Section>

					<Section title="工具" count={profile.tools.length}>
						{profile.tools.length === 0 ? (
							<p className="hint">暂无内置工具</p>
						) : (
							<span className="tags">
								{profile.tools.map((t) => (
									<code key={t}>{t}</code>
								))}
							</span>
						)}
					</Section>

					<Section
						title="skills"
						count={profile.skills.length}
						action={
							<button className="ghost cfg-action" onClick={onManageSkills}>
								管理
							</button>
						}
					>
						{profile.skills.length === 0 ? <p className="hint">暂无 skill</p> : <ResourceRows items={profile.skills} />}
					</Section>

					<Section title="workflows" count={profile.workflows.length}>
						{profile.workflows.length === 0 ? (
							<p className="hint">暂无 workflow</p>
						) : (
							<ResourceRows items={profile.workflows} prefix="/" />
						)}
					</Section>

					<McpSection profile={profile} onSaved={onChanged} />

					{profile.packages.length > 0 && (
						<Section title="依赖包" count={profile.packages.length}>
							<ul className="cfg-rows">
								{profile.packages.map((p) => (
									<Row
										key={p.source}
										name={<code>{p.source}</code>}
										note={p.installed ? undefined : "未安装，请运行 agent check"}
									/>
								))}
							</ul>
						</Section>
					)}
				</div>
			</div>
		</div>
	);
}
